//* *Third-pary components & libraries
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

//* *React Imports
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Authorization from 'components/global/Authorization';
import ImportByTemplate from 'components/global/ImportByTemplate';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
//* *Custom Hooks
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

//* *Constants
import { AUTOCOMPLETE, ENUMS, FORM_IMPORT, WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faDownload, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';

//* * Features

export const InboundForm = NiceModal.create(({ mode, id, modalName }) => {
	const isAuthorized = useAccessControl();
	const [isMinimizing, setIsMinimizing] = useState(false);
	const requireds = {
		purchase: ['ingoingWarehouseID', 'ingoingBranchID', 'binLocationID'],
		production: ['ingoingWarehouseID', 'ingoingBranchID', 'binLocationID'],
		disposal: ['outgoingWarehouseID', 'outgoingBranchID', 'exitLocationID', 'outgoingResponsibleID'],
		returnToSupplier: ['outgoingWarehouseID', 'outgoingBranchID', 'exitLocationID', 'outgoingResponsibleID'],
		returnedFromCustomer: ['ingoingWarehouseID', 'ingoingBranchID', 'binLocationID'],
		interDepotTransfer: [
			'ingoingWarehouseID',
			'ingoingBranchID',
			'binLocationID',
			'outgoingWarehouseID',
			'outgoingBranchID',
			'exitLocationID',
			'outgoingResponsibleID'
		]
	};

	const schema = yup
		.object()
		.shape(
			{
				type: yup.string().nullable().required(),
				orderDate: yup.string().nullable(),
				deliveryDate: yup.string().nullable(),
				action: yup.string().nullable(),
				sku: yup.string().nullable(),
				description: yup.string().nullable(),
				ingoingWarehouseID: yup
					.number()
					.nullable()
					.when(['type', 'ingoingBranchID'], {
						is: (type, ingoingBranchID) =>
							requireds[type]?.includes('ingoingWarehouseID') && !ingoingBranchID,
						then: (schema) => schema.required()
					}),
				ingoingBranchID: yup
					.number()
					.nullable()
					.when(['type', 'ingoingWarehouseID'], {
						is: (type, ingoingWarehouseID) =>
							requireds[type]?.includes('ingoingBranchID') && !ingoingWarehouseID,
						then: (schema) => schema.required()
					}),
				ingoingResponsibleID: yup
					.number()
					.nullable()
					.when(['type', 'ingoingBranchID'], {
						is: (type, ingoingBranchID) =>
							requireds[type]?.includes('ingoingResponsibleID') && !ingoingBranchID,
						then: (schema) => schema.required()
					}),
				binLocationID: yup
					.number()
					.nullable()
					.when(['type', 'ingoingBranchID'], {
						is: (type, ingoingBranchID) =>
							requireds[type]?.includes('binLocationID') &&
							isAuthorized('WMS_BinTracking') &&
							!ingoingBranchID,
						then: (schema) => schema.required()
					}),
				outgoingWarehouseID: yup
					.number()
					.nullable()
					.when(['type', 'outgoingBranchID'], {
						is: (type, outgoingBranchID) =>
							requireds[type]?.includes('outgoingWarehouseID') && !outgoingBranchID,
						then: (schema) => schema.required()
					}),
				outgoingBranchID: yup
					.number()
					.nullable()
					.when(['type', 'outgoingWarehouseID'], {
						is: (type, outgoingWarehouseID) =>
							requireds[type]?.includes('outgoingBranchID') && !outgoingWarehouseID,
						then: (schema) => schema.required()
					}),
				outgoingResponsibleID: yup
					.number()
					.nullable()
					.when(['type', 'outgoingBranchID'], {
						is: (type, outgoingBranchID) =>
							requireds[type]?.includes('outgoingResponsibleID') && !outgoingBranchID,
						then: (schema) => schema.required()
					}),
				exitLocationID: yup
					.number()
					.nullable()
					.when(['type', 'outgoingBranchID'], {
						is: (type, outgoingBranchID) =>
							requireds[type]?.includes('exitLocationID') &&
							isAuthorized('WMS_BinTracking') &&
							!outgoingBranchID,
						then: (schema) => schema.required()
					}),
				products: yup.array().of(
					yup
						.object()
						.shape({
							itemID: yup.number().nullable(),
							sku: yup.string().nullable().required(),
							productID: yup.number().nullable().required(),
							quantity: yup.number().nullable().min(0).required(),
							binLocationID: yup.number().nullable()
						})
						.nullable()
				)
			},
			[
				['ingoingBranchID', 'ingoingWarehouseID'],
				['outgoingBranchID', 'outgoingWarehouseID']
			]
		)
		.required();

	// States
	const [isLoading, setIsLoading] = useState(false);

	// Custom Hooks
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			type: null,
			ingoingWarehouseID: null,
			ingoingBranchID: null,
			binLocationID: null,
			ingoingResponsibleID: null,
			outgoingWarehouseID: null,
			outgoingBranchID: null,
			exitLocationID: null,
			outgoingResponsibleID: null,
			orderDate: null,
			deliveryDate: null,
			status: 'new',
			waybillNumber: null,
			description: null,
			products: [
				{
					sku: null,
					productID: null,
					quantity: null,
					binLocationID: null
				}
			]
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		control,
		watch,
		getValues,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	const { fields, append, remove } = useFieldArray({
		name: 'products',
		control
	});

	const productImportTemplate = {
		icon: faDownload,
		title: 'importByTemplate',
		role: 'importByTemplate',
		downloadUrl: FORM_IMPORT.GET_FORM_IMPORT_PRODUCT_TEMPLATE,
		uploadUrl: FORM_IMPORT.IMPORT_PRODUCT_TEMPLATE,
		acceptedFileTypes: ['.xlsx', '.xls']
	};

	const getFieldsWithType = () => {
		const warehouseTypes = {
			INGOING_WAREHOUSE: {
				key: 'warehouseID',
				value: 'ingoingWarehouseID'
			},
			OUTGOING_WAREHOUSE: {
				key: 'warehouseID',
				value: 'outgoingWarehouseID'
			}
		};

		const AUTOCOMPLETE_TYPES = {
			INGOING: {
				name: 'ingoingWarehouseID',
				autoCompleteName: AUTOCOMPLETE.WAREHOUSES,
				label: 'ingoingWarehouse',
				hidden: !!watch('ingoingBranchID')
			},
			INGOING_RESPONSIBLE: {
				name: 'ingoingResponsibleID',
				autoCompleteName: AUTOCOMPLETE.OTO_PACK_USERS_BY_WAREHOUSE,
				label: 'ingoingResponsible',
				filterObject: warehouseTypes.INGOING_WAREHOUSE,
				hidden: !watch('ingoingWarehouseID')
			},
			BIN_LOCATION: {
				name: 'binLocationID',
				autoCompleteName: AUTOCOMPLETE.RECEIVE_BINS,
				role: 'WMS_BinTracking',
				label: 'receiveLocation',
				filterObject: warehouseTypes.INGOING_WAREHOUSE,
				hidden: !watch('ingoingWarehouseID')
			},
			OUTGOING: {
				name: 'outgoingWarehouseID',
				autoCompleteName: AUTOCOMPLETE.WAREHOUSES,
				label: 'outgoingWarehouse',
				hidden: !!watch('outgoingBranchID')
			},
			OUTGOING_RESPONSIBLE: {
				name: 'outgoingResponsibleID',
				autoCompleteName: AUTOCOMPLETE.OTO_PACK_USERS_BY_WAREHOUSE,
				label: 'outgoingResponsible',
				filterObject: warehouseTypes.OUTGOING_WAREHOUSE,
				hidden: !watch('outgoingWarehouseID')
			},
			EXIT_LOCATION: {
				name: 'exitLocationID',
				autoCompleteName: AUTOCOMPLETE.BINS_BY_WAREHOUSE,
				label: 'exitLocation',
				filterObject: warehouseTypes.OUTGOING_WAREHOUSE,
				hidden: !watch('outgoingWarehouseID'),
				role: 'WMS_BinTracking'
			},
			INGOING_BRANCH: {
				name: 'ingoingBranchID',
				autoCompleteName: AUTOCOMPLETE.BRANCHES,
				label: 'ingoingBranch',
				hidden: !!watch('ingoingWarehouseID')
			},
			OUTGOING_BRANCH: {
				name: 'outgoingBranchID',
				autoCompleteName: AUTOCOMPLETE.BRANCHES,
				label: 'outgoingBranch',
				hidden: !!watch('outgoingWarehouseID')
			}
		};

		switch (watch('type')) {
			case 'purchase':
			case 'production':
			case 'returnedFromCustomer':
				return [
					AUTOCOMPLETE_TYPES.INGOING,
					AUTOCOMPLETE_TYPES.INGOING_BRANCH,
					AUTOCOMPLETE_TYPES.BIN_LOCATION,
					AUTOCOMPLETE_TYPES.INGOING_RESPONSIBLE
				];
			case 'disposal':
			case 'returnToSupplier':
				return [
					AUTOCOMPLETE_TYPES.OUTGOING,
					AUTOCOMPLETE_TYPES.OUTGOING_BRANCH,
					AUTOCOMPLETE_TYPES.EXIT_LOCATION,
					AUTOCOMPLETE_TYPES.OUTGOING_RESPONSIBLE
				];
			case 'interDepotTransfer':
				return [
					AUTOCOMPLETE_TYPES.INGOING,
					AUTOCOMPLETE_TYPES.INGOING_BRANCH,
					AUTOCOMPLETE_TYPES.OUTGOING,
					AUTOCOMPLETE_TYPES.OUTGOING_BRANCH,
					AUTOCOMPLETE_TYPES.BIN_LOCATION,
					AUTOCOMPLETE_TYPES.EXIT_LOCATION,
					AUTOCOMPLETE_TYPES.INGOING_RESPONSIBLE,
					AUTOCOMPLETE_TYPES.OUTGOING_RESPONSIBLE
				];
		}
	};

	const InputsWithType = () => {
		const autocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

		const fields = getFieldsWithType();

		return fields
			?.filter((field) => !field.hidden)
			.map((field, index) => (
				<Authorization key={index} path={field.role}>
					<FormItem label={field.label} colSpan={{ md: 6 }} required={field.name !== 'ingoingResponsibleID'}>
						<AutoComplete
							name={field.name}
							autoCompleteName={field.autoCompleteName}
							columns={autocompleteColumns}
							selectionLabelAccessor={['Name']}
							value={watch(field.name)}
							disabled={
								mode === 'view' || (field?.filterObject ? !watch(field?.filterObject?.value) : false)
							}
							setValue={(value, firstRender) => {
								if (firstRender) return;
								if (field.name === 'ingoingWarehouseID') {
									setValue('binLocationID', null);
									setValue('ingoingResponsibleID', null);
								}
								if (field.name === 'outgoingWarehouseID') {
									setValue('outgoingResponsibleID', null);
									setValue('exitLocationID', null);
								}
								setValue(field.name, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							{...(field.filterObject
								? {
										filterObject: {
											[field.filterObject.key]: watch(field.filterObject.value)
										}
									}
								: {})}
						/>
					</FormItem>
				</Authorization>
			));
	};

	usePrompt(mode !== 'view' && isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);
	const productAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'SKU', width: 100 },
			{ accessor: 'barcode', width: 100 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);

	const binAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const tableColumns = useMemo(
		() => [
			{
				id: 'sku',
				header: 'productName',
				required: true,
				size: 150,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						name={`products[${row.index}].productID`}
						value={watch(`products[${row.index}].productID`)}
						setValue={(value) => {
							setValue(`products[${row.index}].productID`, value?.ID ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
							setValue(`products[${row.index}].sku`, value?.SKU ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['SKU', 'name']}
						viewMode
					/>
				),
				editCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						name={`products[${row.index}].productID`}
						value={watch(`products[${row.index}].productID`)}
						setValue={(value) => {
							setValue(`products[${row.index}].productID`, value?.ID ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
							setValue(`products[${row.index}].sku`, value?.SKU ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['SKU', 'name']}
						disabled={mode === 'view'}
					/>
				)
			},
			{
				id: 'quantity',
				header: 'quantity',
				required: true,
				showOnMobile: true,
				renderCell: ({ row }) => <StringInput name={`products[${row.index}].quantity`} viewMode />,
				editCell: ({ row }) => (
					<StringInput name={`products[${row.index}].quantity`} disabled={mode === 'view'} />
				),
				size: 75
			},
			...(mode === 'view' && isAuthorized('WMSAdvanceModule')
				? [
						{
							id: 'binLocationID',
							header: 'binLocation',
							size: 150,
							accessorKey: 'binLocationID',
							renderCell: ({ row }) => (
								<AutoComplete
									name={`products[${row.index}].binLocationID`}
									autoCompleteName={AUTOCOMPLETE.BINS}
									value={row.original.binLocationID}
									columns={binAutocompleteColumns}
									selectionLabelAccessor={['Name']}
									viewMode={true}
									disabled={true}
								/>
							)
						}
					]
				: []),
			...(mode === 'edit' || mode === 'add'
				? [
						{
							id: 'deleteRow',
							size: 30,
							maxSize: 30,
							showOnMobile: true
						}
					]
				: [])
		],
		[]
	);
	// Helpers
	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = (data) => {
		setIsLoading(true);
		// Determine the url whether mode is edit or add
		const URL = !id ? WMS_MODULE.ADD_INBOUND : WMS_MODULE.UPDATE_INBOUND;

		// Preparing payload to send
		const { products, ...rest } = data;
		const payload = {
			...rest,

			items: products.map(
				({
					itemID = null,
					expiryDate = null,
					lotNumber = null,
					quantity = null,
					sku = null,
					productID = null,
					binLocationID = null
				}) => ({
					itemID,
					expiryDate,
					lotNumber,
					quantity,
					sku,
					productID,
					binLocationID
				})
			)
		};
		if (id) payload.ID = Number(id);

		// Post data to api
		axiosPrivate
			.post(URL, payload)
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListsInPage());
					handleCloseModal();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	};
	const fillForm = useCallback((id) => {
		setIsLoading(true);
		axiosPrivate
			.post(WMS_MODULE.GET_INBOUND, { ID: Number(id) })
			.then((response) => {
				const { data: responseData, status } = response;
				if (status === 200) {
					// reset form fields
					const { ...rest } = responseData;
					reset({
						// Details
						type: rest.type ?? null,
						orderDate: rest.orderDate ?? null,
						deliveryDate: rest.deliveryDate ?? null,
						status: rest.status ?? 'new',
						waybillNumber: rest.waybillNumber ?? null,
						description: rest.description ?? null,
						ingoingWarehouseID: rest.ingoingWarehouseID ?? null,
						ingoingBranchID: rest.ingoingBranchID ?? null,
						binLocationID: rest.binLocationID ?? null,
						ingoingResponsibleID: rest.ingoingResponsibleID ?? null,
						outgoingWarehouseID: rest.outgoingWarehouseID ?? null,
						outgoingBranchID: rest.outgoingBranchID ?? null,
						exitLocationID: rest.exitLocationID ?? null,
						outgoingResponsibleID: rest.outgoingResponsibleID ?? null,
						// Order Items
						products: responseData.items?.map((item) => ({
							itemID: item.itemID ?? null,
							productID: item.productID ?? null,
							expiryDate: item.expiryDate ?? null,
							lotNumber: item.lotNumber ?? null,
							quantity: item.quantity ?? null,
							binLocationID: item.binLocationID ?? null,
							sku: item.sku ?? null
						}))
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				handleCloseModal();
				showError();
			})
			.finally(() => setIsLoading(false));
	}, []);

	// Statics
	const defaultColSpan = {
		md: 6
	};

	// Effects
	useEffect(() => {
		if (id) fillForm(id);
	}, [id]);

	useEffect(() => {
		const ingoingResponsibleID = watch('ingoingResponsibleID');
		const outgoingResponsibleID = watch('outgoingResponsibleID');
		if (ingoingResponsibleID || outgoingResponsibleID) setValue('status', 'assigned');
		else setValue('status', 'new');
	}, [watch('ingoingResponsibleID'), watch('outgoingResponsibleID')]);

	const getTitle = () =>
		mode === 'view' ? 'viewInventoryOrder' : mode === 'edit' ? 'editInventoryOrder' : 'addInventoryOrder';

	const tableData = watch('tableData');
	const deleteRow = ({ index }) => {
		remove(index);
	};

	useEffect(() => {
		if (tableData) {
			// if you has default row
			const { productID, quantity } = watch('products[0]');

			if (!productID && !quantity && mode !== 'edit' && mode !== 'view') {
				deleteRow({ index: 0 });
			}

			const newProducts = tableData.map((item) => ({
				sku: item.SKU,
				productID: item.productID,
				quantity: item.quantity,
				binLocationID: item.binLocationID
			}));

			append(newProducts);

			const existingProducts = getValues('products');

			newProducts.forEach((item, index) => {
				if (index >= existingProducts.length) {
					['sku', 'productID', 'quantity', 'binLocationID'].forEach((field) => {
						setValue(`products[${index}].${field}`, item[field], {
							shouldDirty: true,
							shouldValidate: true
						});
					});
				}
			});
		}
	}, [tableData]);

	// Rendering
	return (
		<Modal
			name={modalName}
			maxWidth="large"
			className="modal-inboundForm"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={getTitle()}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{getTitle()}</Typography>
			</ModalTitle>
			<ModalContent style={{ width: '100%' }}>
				<Form formMethods={formMethods}>
					{/* type */}
					<FormItem label="type" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							name="type"
							enumName={ENUMS.INV_TRANS_TYPE}
							placeholder="selectOne"
							value={useWatch({ control, name: 'type' })}
							disabled={mode === 'view'}
							setValue={(value) => {
								setValue('type', value, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('ingoingWarehouseID', null);
								setValue('outgoingWarehouseID', null);
								setValue('ingoingBranchID', null);
								setValue('outgoingBranchID', null);
								setValue('binLocationID', null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('exitLocationID', null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					{/* type */}

					{/* orderDate */}
					<FormItem label="orderDate" colSpan={defaultColSpan} isLoading={isLoading}>
						<DateInput
							name="orderDate"
							value={useWatch({ control, name: 'orderDate' })}
							disabled={mode === 'view'}
							setValue={(value) => {
								setValue('orderDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{/* orderDate */}

					{/* deliveryDate */}
					<FormItem label="deliveryDate" colSpan={defaultColSpan} isLoading={isLoading}>
						<DateInput
							name="deliveryDate"
							value={useWatch({ control, name: 'deliveryDate' })}
							disabled={mode === 'view'}
							setValue={(value) => {
								setValue('deliveryDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{/* deliveryDate */}

					{/* status */}
					<FormItem label="status" colSpan={defaultColSpan} isLoading={isLoading}>
						<Status name="status" value={watch('status')} disabled={mode === 'view'} />
					</FormItem>
					{/* status */}

					{/* waybillNumber */}
					<FormItem label="waybillNumber" colSpan={{ md: 12 }} isLoading={isLoading}>
						<StringInput name="waybillNumber" disabled={mode === 'view'} />
					</FormItem>
					{/* waybillNumber */}

					<InputsWithType />

					{/* description */}
					<FormItem label="description" isLoading={isLoading}>
						<TextArea name="description" disabled={mode === 'view'} />
					</FormItem>
					{/* description */}

					{/* orderItems */}
					<Typography variant="p1" className="my-4">
						items
					</Typography>
					<Table
						columns={tableColumns}
						data={fields}
						loading={isLoading}
						onRowDelete={deleteRow}
						initEditMode={() => true}
						footerComponent={
							mode !== 'view' ? (
								<div className="mt-4" style={{ display: 'flex' }}>
									<Button
										className="mx-3"
										variant="blackOutline"
										label="addNewRow"
										icon={faPlusCircle}
										onClick={() => {
											append({});
										}}
									/>
									<ImportByTemplate setValue={setValue} importActionDetails={productImportTemplate} />
								</div>
							) : null
						}
					/>
					{/* orderItems */}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						label={mode === 'edit' ? 'save' : 'add'}
						disabled={!isDirty || isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default InboundForm;
